import axios from 'axios';

// Get API key from environment variables
const GOOGLE_TRANSLATE_API_KEY = 'AIzaSyDMOJDTD3_QTWzRUxlQv7NipLJiygN2aeQ' // process.env.REACT_APP_GOOGLE_TRANSLATE_API_KEY ; 
const GOOGLE_TRANSLATE_API_URL = 'https://translation.googleapis.com/language/translate/v2';

const translationCache = {}; // Cache for translations to avoid repeated API calls

/**
 * Translates text to the target language using Google Translate API
 * @param {string} text - Text to translate
 * @param {string} targetLang - Target language code (e.g., 'en', 'bg', 'de', 'es', 'fr', ...)
 * @param {string} sourceLang - Source language code (default: 'en')
 * @returns {Promise<string>} - Translated text
 */
export const translateText = async (text, targetLang, sourceLang = 'en') => {
  // If target language is the same as source, return original text
  if (targetLang === sourceLang) { return text; }

  // Check cache first
  const cacheKey = `${text}_${targetLang}_${sourceLang}`;
  if (translationCache[cacheKey]) { return translationCache[cacheKey]; }

  try {
    const response = await axios.post(
      `${GOOGLE_TRANSLATE_API_URL}?key=${GOOGLE_TRANSLATE_API_KEY}`,
      {
        q: text,
        target: targetLang,
        source: sourceLang,
        format: 'text'
      }
    );

    const translatedText = response.data.data.translations[0].translatedText;
    
    translationCache[cacheKey] = translatedText; // Cache the result
    
    return translatedText;
  } catch (error) {
    console.error('Translation error:', error);
    return text; // Return original text if translation fails
  }
};

/**
 * Batch translates multiple texts in a single API call
 * @param {Array<string>} texts - Array of texts to translate
 * @param {string} targetLang - Target language code
 * @param {string} sourceLang - Source language code
 * @returns {Promise<Array<string>>} - Array of translated texts
 */
export const textTranslate = async (texts, targetLang, sourceLang = 'en') => {
  // If target language is the same as source, return original texts
  if (targetLang === sourceLang) { return texts; }

  // Filter out texts that are already in cache
  const uncachedTexts = [];
  const cachedResults = [];
  const cacheIndices = [];

  texts.forEach((text, index) => {
    const cacheKey = `${text}_${targetLang}_${sourceLang}`;
    if (translationCache[cacheKey]) {
      cachedResults[index] = translationCache[cacheKey];
    } else {
      uncachedTexts.push(text);
      cacheIndices.push(index);
    }
  });

  // If all texts are cached, return them
  if (uncachedTexts.length === 0) { return texts.map((_, index) => cachedResults[index] || texts[index]); }

  try {
    const response = await axios.post(
      `${GOOGLE_TRANSLATE_API_URL}?key=${GOOGLE_TRANSLATE_API_KEY}`,
      {
        q: uncachedTexts,
        target: targetLang,
        source: sourceLang,
        format: 'text'
      }
    );

    const translations = response.data.data.translations;
    
    // Cache the results and build the final array
    const result = [...texts];
    translations.forEach((translation, i) => {
      const originalText = uncachedTexts[i];
      const cacheKey = `${originalText}_${targetLang}_${sourceLang}`;
      const translatedText = translation.translatedText;
      
      translationCache[cacheKey] = translatedText; // Cache the result
      
      result[cacheIndices[i]] = translatedText; // Add to the result array at the correct index
    });

    // Add cached results
    Object.keys(cachedResults).forEach(index => {
      result[index] = cachedResults[index];
    });

    return result; // Return translated text
  } catch (error) {
    console.error('Batch translation error:', error);
    return texts; // Return original texts if translation fails
  }
}; 